import { ChoiceCsvExport, ChoiceDashboardFilters, ChoiceRowsPage, ChoiceRowsRequest } from "../entities/ChoiceDashboard";
import { ZoneFilter } from "../../presentation/components/pages/ui-utils/zoneFilterTypes";
import { SendDocFormInputs } from "../../presentation/components/widgets/SendIndvDocumentForm";
import CampaignRepository from "../data/CampaignRepository";
import ChartRepository from "../data/ChartRepository";
import DocumentRepository from "../data/DocumentRepository";
import DocumentTemplateRepository from "../data/DocumentTemplateRepository";
import ExternalSchoolIntegration from "../data/ExternalSchoolIntegration";
import MessageRepository from "../data/MessageRepository";
import PackageRepository from "../data/PackageRepository";
import PostgisRepository from "../data/PostgisRepository";
import SelectRoutingRepository from "../data/SelectRoutingRepository";
import SignatoryGroupRepository from "../data/SignatoryGroupRepository";
import SigningDocument from "../entities/_SigningDocument";
import Campaign, { TargetAudience } from "../entities/Campaign";
import { CampaignChart } from "../entities/Chart";
import Document from "../entities/Document";
import DocumentTemplate from "../entities/DocumentTemplate";
import Message from "../entities/Message";
import Organization from "../entities/Organization";
import Package from "../entities/Package";
import { SectionsData } from "../entities/Section";
import SelectRouting from "../entities/SelectRouting";
import Signatory from "../entities/Signatory";
import SignatoryGroup from "../entities/SignatoryGroup";
import Student from "../entities/Student";
import { PostgisAssignment, PostgisProgram, PostgisSchool, StudentsByDate, StudentsSpatialResult } from "../entities/StudentPostgis";
import User from "../entities/User";

export default class RequestorInteractor {
  private campaignRepository: CampaignRepository;
  private documentRepository: DocumentRepository;
  private documentTemplateRepository: DocumentTemplateRepository;
  private packageRepository: PackageRepository;
  private selectRoutingRepository: SelectRoutingRepository;
  private signatoryGroupRepository: SignatoryGroupRepository;
  private messageRepository: MessageRepository;
  private externalSchoolIntegration: ExternalSchoolIntegration;
  private chartRepository: ChartRepository;
  private postgisRepository: PostgisRepository;

  constructor(
    campaignRepository: CampaignRepository,
    documentRepository: DocumentRepository,
    documentTemplateRepository: DocumentTemplateRepository,
    packageRepository: PackageRepository,
    selectRoutingRepository: SelectRoutingRepository,
    signatoryGroupRepository: SignatoryGroupRepository,
    messageRepository: MessageRepository,
    externalSchoolIntegration: ExternalSchoolIntegration,
    chartRepository: ChartRepository,
    postgisRepository: PostgisRepository
  ) {
    this.campaignRepository = campaignRepository;
    this.documentRepository = documentRepository;
    this.documentTemplateRepository = documentTemplateRepository;
    this.packageRepository = packageRepository;
    this.selectRoutingRepository = selectRoutingRepository;
    this.signatoryGroupRepository = signatoryGroupRepository;
    this.messageRepository = messageRepository;
    this.externalSchoolIntegration = externalSchoolIntegration;
    this.chartRepository = chartRepository;
    this.postgisRepository = postgisRepository;
  }

  public async saveDocumentTemplate(
    user: User,
    documentTemplate: DocumentTemplate
  ): Promise<DocumentTemplate> {
    return this.documentTemplateRepository.saveDocumentTemplate(
      user,
      documentTemplate
    );
  }
  public async deleteDocumentTemplate(
    user: User,
    documentTemplate: DocumentTemplate
  ): Promise<boolean> {
    return this.documentTemplateRepository.deleteDocumentTemplate(
      user,
      documentTemplate
    );
  }
  public async getDocumentTemplate(
    user: User,
    documentTemplateId: string
  ): Promise<DocumentTemplate | undefined> {
    return this.documentTemplateRepository.getDocumentTemplate(
      user,
      documentTemplateId
    );
  }
  public async loadDocTemplateByCampaign(
    userId: string,
    organizationId: string,
    campaignId: string
  ): Promise<DocumentTemplate | undefined> {
    return this.documentTemplateRepository.loadDocTemplateByCampaign(
      userId,
      organizationId,
      campaignId
    );
  }
  public async getDocumentTemplates(
    user: User,
    documentPackage?: Package,
    includeFiles?: boolean
  ): Promise<DocumentTemplate[]> {
    return this.documentTemplateRepository.getDocumentTemplates(
      user,
      documentPackage,
      includeFiles
    );
  }
  public async documentTemplateSignaturePosition(
    user: User,
    documentTemplate: DocumentTemplate,
    file: string
  ): Promise<DocumentTemplate> {
    return await this.documentTemplateRepository.documentTemplateSignaturePosition(
      user,
      documentTemplate,
      file
    );
  }

  public async getPackages(user: User): Promise<Package[]> {
    return this.packageRepository.getPackages(user);
  }
  public async savePackage(user: User, objPackage: Package): Promise<Package> {
    return this.packageRepository.savePackage(user, objPackage);
  }
  public async deletePackage(
    user: User,
    objPackage: Package
  ): Promise<Boolean> {
    return this.packageRepository.deletePackage(user, objPackage);
  }

  public async saveCampaign(
    user: User,
    campaign: Campaign,
    signatories?: Signatory[]
  ): Promise<Campaign> {
    return this.campaignRepository.saveCampaign(user, campaign, signatories);
  }

  public async getCampaign(user: User, campaignId: string): Promise<Campaign> {
    return this.campaignRepository.getCampaign(user, campaignId);
  }

  public async getCampaignExt(shortid: string): Promise<Campaign> {
    return this.campaignRepository.getCampaignExt(shortid);
  }

  public async deleteCampaign(
    user: User,
    campaign: Campaign,
    targetAudience: TargetAudience
  ): Promise<Boolean> {
    return this.campaignRepository.deleteCampaign(
      user,
      campaign,
      targetAudience
    );
  }

  public async getCampaigns(
    user: User,
    targetAudience: TargetAudience
  ): Promise<Campaign[]> {
    return this.campaignRepository.getCampaigns(user, targetAudience);
  }

  public async getCampaignsPagination(
    user: User,
    targetAudience: TargetAudience,
    pageSize: number,
    lastEvaluatedKey?: any
  ): Promise<{ items: Campaign[]; lastEvaluatedKey?: any }> {
    return this.campaignRepository.getCampaignsPagination(
      user,
      targetAudience,
      pageSize,
      lastEvaluatedKey
    );
  }

  public async getCampaignsbyLookup(
    user: User,
    targetAudience: TargetAudience,
    lookupType: string,
    lookupValue: string
  ): Promise<Campaign[]> {
    return this.campaignRepository.getCampaignsbyLookup(
      user,
      targetAudience,
      lookupType,
      lookupValue
    );
  }

  public async getCampaignsByLookupPagination(
    user: User,
    targetAudience: TargetAudience,
    lookupType: string,
    lookupValue: string,
    pageSize: number,
    lastEvaluatedKey?: any,
    existingCampaignIds?: any
  ): Promise<{ items: Campaign[]; lastEvaluatedKey?: any }> {
    return this.campaignRepository.getCampaignsByLookupPagination(
      user,
      targetAudience,
      lookupType,
      lookupValue,
      pageSize,
      lastEvaluatedKey,
      existingCampaignIds
    );
  }

  public async getAllCampaigns(user: User): Promise<Campaign[]> {
    return this.campaignRepository.getAllCampaigns(user);
  }

  public async getCampaignsCount(
    user: User,
    targetAudience: TargetAudience
  ): Promise<number> {
    return this.campaignRepository.getCampaignsCount(user, targetAudience);
  }

  public async getCampaignsLookupsCount(
    user: User,
    targetAudience: TargetAudience,
    lookupType: string,
    lookupValue: string
  ): Promise<number> {
    return this.campaignRepository.getCampaignsLookupsCount(
      user,
      targetAudience,
      lookupType,
      lookupValue
    );
  }

  public async sendCampaign(
    user: User,
    campaign: Campaign
  ): Promise<string | undefined> {
    return this.campaignRepository.sendCampaign(user, campaign);
  }

  public async sendDocumentIndividual(
    formData: SendDocFormInputs
  ): Promise<boolean> {
    return this.documentRepository.sendDocumentIndividual(formData);
  }

  public async getSigningDocument(
    user: User,
    campaign: Campaign
  ): Promise<SigningDocument[]> {
    return this.campaignRepository.getSigningDocument(user, campaign);
  }

  public async getCampaignEmailTracking(
    user: User,
    campaign: Campaign
  ): Promise<{ tracking: any; stats: any }> {
    return this.campaignRepository.getCampaignEmailTracking(user, campaign);
  }

  public async getMessageEmailTracking(
    user: User,
    message: Message
  ): Promise<{ tracking: any; stats: any }> {
    return this.messageRepository.getMessageEmailTracking(user, message);
  }

  public async getDocuments(
    user: User,
    campaign: Campaign
  ): Promise<Document[]> {
    return this.documentRepository.getDocuments(user, campaign);
  }

  public async processDocumentStaffbyCampaign(
    campaign: Campaign
  ): Promise<Document> {
    return this.documentRepository.processDocumentStaffbyCampaign(campaign);
  }

  public async saveSignatoryGroup(user: User, signatoryGroup: SignatoryGroup) {
    return this.signatoryGroupRepository.saveSignatoryGroup(
      user,
      signatoryGroup
    );
  }
  public async saveSignatoryGroupAsync(
    user: User,
    signatoryGroup: SignatoryGroup
  ) {
    return this.signatoryGroupRepository.saveSignatoryGroupAsync(
      user,
      signatoryGroup
    );
  }
  public async deleteSignatoryGroup(
    user: User,
    signatoryGroup: SignatoryGroup
  ) {
    return this.signatoryGroupRepository.deleteSignatoryGroup(
      user,
      signatoryGroup
    );
  }
  public async getSignatoryGroups(user: User) {
    return this.signatoryGroupRepository.getSignatoryGroups(user);
  }

  public async saveMessage(user: User, message: Message): Promise<Message> {
    return this.messageRepository.saveMessage(user, message);
  }

  public async getMessage(user: User, messageId: string): Promise<Message> {
    return this.messageRepository.getMessage(user, messageId);
  }

  public async getMessages(
    user: User,
    targeAudience: TargetAudience
  ): Promise<Message[]> {
    return this.messageRepository.getMessages(user, targeAudience);
  }

  public async getMessagesbyLookup(
    user: User,
    targetAudience: TargetAudience,
    lookupType: string,
    lookupValue: string
  ): Promise<Message[]> {
    return this.messageRepository.getMessagesbyLookup(
      user,
      targetAudience,
      lookupType,
      lookupValue
    );
  }

  public async getMessagesBySignatory(
    user: User,
    signatoryEmail: string
  ): Promise<Message[]> {
    return this.messageRepository.getMessagesBySignatory(user, signatoryEmail);
  }

  public async deleteMessage(
    user: User,
    message: Message,
    targeAudience: TargetAudience
  ): Promise<Boolean> {
    return this.messageRepository.deleteMessage(user, message, targeAudience);
  }

  public async getMessagesPagination(
    user: User,
    targetAudience: TargetAudience,
    pageSize: number,
    lastEvaluatedKey?: any
  ): Promise<{ items: Message[]; lastEvaluatedKey?: any }> {
    return this.messageRepository.getMessagesPagination(
      user,
      targetAudience,
      pageSize,
      lastEvaluatedKey
    );
  }

  public async getMessagesByLookupPagination(
    user: User,
    targetAudience: TargetAudience,
    lookupType: string,
    lookupValue: string,
    pageSize: number,
    lastEvaluatedKey?: any,
    existingMessageIds?: any
  ): Promise<{ items: Message[]; lastEvaluatedKey?: any }> {
    return this.messageRepository.getMessagesByLookupPagination(
      user,
      targetAudience,
      lookupType,
      lookupValue,
      pageSize,
      lastEvaluatedKey,
      existingMessageIds
    );
  }

  public async getMessagesCount(
    user: User,
    targetAudience: TargetAudience
  ): Promise<number> {
    return this.messageRepository.getMessagesCount(user, targetAudience);
  }

  public async getMessagesLookupsCount(
    user: User,
    targetAudience: TargetAudience,
    lookupType: string,
    lookupValue: string
  ): Promise<number> {
    return this.messageRepository.getMessagesLookupsCount(
      user,
      targetAudience,
      lookupType,
      lookupValue
    );
  }

  public async sendMessage(
    user: User,
    message: Message
  ): Promise<string | undefined> {
    return this.messageRepository.sendMessage(user, message);
  }

  public async getSections(
    cleverToken: string,
    schoolId: string
  ): Promise<SectionsData[]> {
    const sections = await this.externalSchoolIntegration.getSections(
      cleverToken,
      schoolId
    );
    return sections;
  }

  public async getGrades(
    organization: Organization,
    schoolYearStart: number,
    schoolYearEnd: number
  ): Promise<string[]> {
    return this.externalSchoolIntegration.getGrades(
      organization,
      schoolYearStart,
      schoolYearEnd
    );
  }
  public async getStudents(
    organization: Organization,
    schoolYearStart: number,
    schoolYearEnd: number,
    grade: string
  ): Promise<Student[]> {
    return this.externalSchoolIntegration.getStudents(
      organization,
      schoolYearStart,
      schoolYearEnd,
      grade
    );
  }
  public async getStudentSignatories(
    organization: Organization,
    schoolYearStart: number,
    schoolYearEnd: number,
    grade: string,
    student: Student
  ): Promise<Signatory[]> {
    return this.externalSchoolIntegration.getStudentSignatories(
      organization,
      schoolYearStart,
      schoolYearEnd,
      grade,
      student
    );
  }
  public async getSignatories(
    organization: Organization,
    schoolYearStart: number,
    schoolYearEnd: number,
    grade: string
  ): Promise<Signatory[]> {
    const signatories: Signatory[] = [];
    try {
      const students = await this.externalSchoolIntegration.getStudents(
        organization,
        schoolYearStart,
        schoolYearEnd,
        grade
      );
      students.forEach((student) => {
        signatories.push(...student.signatories);
      });
    } catch (e) {
      console.log(e);
    }
    return signatories;
  }

  public async getCampaignChart(user: User): Promise<CampaignChart[]> {
    return this.chartRepository.getCampaignChart(user);
  }

  public async getRequestors(): Promise<User[]> {
    return this.getRequestors();
  }

  public async saveSendCampaign(
    user: User,
    campaign: Campaign
  ): Promise<boolean> {
    return this.campaignRepository.saveSendCampaign(user, campaign);
  }

  public async saveSelectRouting(user: User, selectRouting: SelectRouting) {
    return this.selectRoutingRepository.saveSelectRouting(
      user,
      selectRouting
    );
  }
  public async deleteSelectRouting(
    user: User,
    selectRouting: SelectRouting
  ) {
    return this.selectRoutingRepository.deleteSelectRouting(
      user,
      selectRouting
    );
  }
  public async getSelectRoutings(user: User) {
    return this.selectRoutingRepository.getSelectRoutings(user);
  }

  public async getSchoolsZone(organizationId: string): Promise<PostgisSchool[]> {
    return this.postgisRepository.getSchoolsZone(organizationId);
  }

  public async getGradesZone(organizationId: string): Promise<string[]> {
    return this.postgisRepository.getGradesZone(organizationId);
  }

  public async getGradesZoneFiltered(
    organizationId: string,
    zoneFilter: ZoneFilter,
    schoolIds: string[]
  ): Promise<string[]> {
    return this.postgisRepository.getGradesZoneFiltered(organizationId, zoneFilter, schoolIds);
  }

  public async getGradesBySchools(organizationId: string, schoolIds: string[]): Promise<string[]> {
    return this.postgisRepository.getGradesBySchools(organizationId, schoolIds);
  }

  public async getProgramsZone(organizationId: string): Promise<PostgisProgram[]> {
    return this.postgisRepository.getProgramsZone(organizationId);
  }

  public async getProgramsZoneFiltered(
    organizationId: string,
    zoneFilter: ZoneFilter,
    schoolIds: string[],
    grades: string[]
  ): Promise<PostgisProgram[]> {
    return this.postgisRepository.getProgramsZoneFiltered(
      organizationId,
      zoneFilter,
      schoolIds,
      grades
    );
  }

  public async getProgramsBySchools(
    organizationId: string,
    schoolIds: string[],
    grades: string[]
  ): Promise<PostgisProgram[]> {
    return this.postgisRepository.getProgramsBySchools(organizationId, schoolIds, grades);
  }

  public async getAssignmentsZone(organizationId: string): Promise<PostgisAssignment[]> {
    return this.postgisRepository.getAssignmentsZone(organizationId);
  }

  public async getAssignmentsZoneFiltered(
    organizationId: string,
    zoneFilter: ZoneFilter,
    schoolIds: string[],
    grades: string[]
  ): Promise<PostgisAssignment[]> {
    return this.postgisRepository.getAssignmentsZoneFiltered(
      organizationId,
      zoneFilter,
      schoolIds,
      grades
    );
  }

  public async getAssignmentsBySchools(
    organizationId: string,
    schoolIds: string[],
    grades: string[]
  ): Promise<PostgisAssignment[]> {
    return this.postgisRepository.getAssignmentsBySchools(organizationId, schoolIds, grades);
  }

  public async getPostgisStudentsZone(
    organizationId: string,
    zoneFilter: ZoneFilter,
    schoolIds: string[],
    grades: string[],
    programs: string[],
    assignments: string[]
  ): Promise<{ students: StudentsSpatialResult[], excludedCount: number }> {
    return this.postgisRepository.getPostgisStudentsZone(
      organizationId,
      zoneFilter,
      schoolIds,
      grades,
      programs,
      assignments
    );
  }

  public async getPostgisStudentsByFilters(
    organizationId: string,
    schoolIds: string[],
    grades: string[],
    programs: string[]
  ): Promise<{ students: StudentsSpatialResult[], excludedCount: number }> {
    return this.postgisRepository.getPostgisStudentsByFilters(
      organizationId,
      schoolIds,
      grades,
      programs
    );
  }

  public async getArchiveDates(organizationId: string): Promise<string[]> {
    return this.postgisRepository.getArchiveDates(organizationId);
  }

  public async getArchivedStudentNumberForGradesAndPrograms(organizationId: string, archiveDate: string, schoolId: string): Promise<StudentsByDate> {
    return this.postgisRepository.getArchivedStudentNumberForGradesAndPrograms(organizationId, archiveDate, schoolId);
  }
  public async getCompareStudentsCountByGradeAndProgram(organizationId: string, archiveDate1: string, archiveDate2: string, schoolId: string): Promise<StudentsByDate> {
    return this.postgisRepository.getCompareStudentsCountByGradeAndProgram(organizationId, archiveDate1, archiveDate2, schoolId);
  }

  public async getArchivedStudentNumberForGradesAndProgramsAllSchools(organizationId: string, archiveDate: string): Promise<StudentsByDate> {
    return this.postgisRepository.getArchivedStudentNumberForGradesAndProgramsAllSchools(organizationId, archiveDate);
  }

  public async getCompareStudentsCountByGradeAndProgramAllSchools(organizationId: string, archiveDate1: string, archiveDate2: string): Promise<StudentsByDate> {
    return this.postgisRepository.getCompareStudentsCountByGradeAndProgramAllSchools(organizationId, archiveDate1, archiveDate2);
  }

  public async getStudentsCountBySchoolGradeAndProgram(organizationId: string, archiveDate: string): Promise<any> {
    return this.postgisRepository.getStudentsCountBySchoolGradeAndProgram(organizationId, archiveDate);
  }

  public async getProgramDemand(organizationId: string): Promise<any> {
    return this.postgisRepository.getProgramDemand(organizationId);
  }

  public async getProgramApplicants(organizationId: string, program: string): Promise<any[]> {
    return this.postgisRepository.getProgramApplicants(organizationId, program);
  }

  public async getChoiceDashboard(organizationId: string, filters: ChoiceDashboardFilters): Promise<any> {
    return this.postgisRepository.getChoiceDashboard(organizationId, filters);
  }

  public async getChoiceRows(organizationId: string, request: ChoiceRowsRequest): Promise<ChoiceRowsPage> {
    return this.postgisRepository.getChoiceRows(organizationId, request);
  }

  public async exportChoiceRows(organizationId: string, filters: ChoiceDashboardFilters): Promise<ChoiceCsvExport> {
    return this.postgisRepository.exportChoiceRows(organizationId, filters);
  }

  public async startChoiceImport(organizationId: string): Promise<any> {
    return this.postgisRepository.startChoiceImport(organizationId);
  }

  public async getChoiceYears(organizationId: string): Promise<{ schoolYears: string[] }> {
    return this.postgisRepository.getChoiceYears(organizationId);
  }

  public async getChoiceImportStatus(organizationId: string): Promise<any> {
    return this.postgisRepository.getChoiceImportStatus(organizationId);
  }

  public async getExitingStudents(organizationId: string, school: string): Promise<any[]> {
    return this.postgisRepository.getExitingStudents(organizationId, school);
  }

  public async getProjectedEnrollment(organizationId: string, archiveDate: string, years: number, growthRate?: number): Promise<any> {
    return this.postgisRepository.getProjectedEnrollment(organizationId, archiveDate, years, growthRate);
  }

  public async getSESBreakdownBySchool(organizationId: string, archiveDate: string): Promise<any[]> {
    return this.postgisRepository.getSESBreakdownBySchool(organizationId, archiveDate);
  }

  public async getSESBreakdownByZone(organizationId: string, zoneFilter: ZoneFilter): Promise<any[]> {
    return this.postgisRepository.getSESBreakdownByZone(organizationId, zoneFilter);
  }

  public async getCompareStudentsCountBySchoolGradeAndProgram(organizationId: string, archiveDate1: string, archiveDate2: string): Promise<any> {
    return this.postgisRepository.getCompareStudentsCountBySchoolGradeAndProgram(organizationId, archiveDate1, archiveDate2);
  }

  public async getStudentsCountByZone(organizationId: string, zoneFilter: ZoneFilter): Promise<{ counts: any[]; totalInZone: number; excludedCount: number; averageSes: number | null; sesDistribution: Record<string, number>; inTransportationZone: number; outsideTransportationZone: number; transportationZoneBreakdown: Record<string, number> }> {
    return this.postgisRepository.getStudentsCountByZone(organizationId, zoneFilter);
  }

  public async getSeatingStudentDetails(
    organizationId: string,
    payload: { archiveDate?: string; schoolIds?: string[]; grades?: string[]; programs?: string[]; zoneFilter?: ZoneFilter | null; offset?: number; limit?: number }
  ) {
    return this.postgisRepository.getSeatingStudentDetails(organizationId, payload);
  }

  public async askZoneAI(organizationId: string, payload: any): Promise<any> {
    return this.postgisRepository.askZoneAI(organizationId, payload);
  }

  public async summarizeZoneAI(organizationId: string, payload: any): Promise<any> {
    return this.postgisRepository.summarizeZoneAI(organizationId, payload);
  }

  public async geocodeAddress(organizationId: string, payload: { address: string }): Promise<any> {
    return this.postgisRepository.geocodeAddress(organizationId, payload);
  }

  public async combineZones(organizationId: string, payload: { zoneNames: string[]; operation: string }): Promise<any> {
    return this.postgisRepository.combineZones(organizationId, payload);
  }

  public async bufferZone(organizationId: string, payload: { zoneName: string; distance: number; unit: string }): Promise<any> {
    return this.postgisRepository.bufferZone(organizationId, payload);
  }

  public async scaleZone(organizationId: string, payload: { zoneName: string; percentage: number }): Promise<any> {
    return this.postgisRepository.scaleZone(organizationId, payload);
  }

  public async translateText(text: string, targetLanguage: string): Promise<any> {
    return this.documentRepository.translateText(text, targetLanguage);
  }

  public async getCleverSchools(cleverToken: string): Promise<any[]> {
    return this.externalSchoolIntegration.getSchools(cleverToken);
  }

  public async getCleverStaff(cleverToken: string, schoolId: string): Promise<any[]> {
    return this.externalSchoolIntegration.getStaff(cleverToken, schoolId);
  }

  public async getCleverStudent(cleverToken: string, studentId: string): Promise<any> {
    return this.externalSchoolIntegration.getStudent(cleverToken, studentId);
  }

  public async getCleverContacts(cleverToken: string, studentId: string): Promise<any> {
    return this.externalSchoolIntegration.getContacts(cleverToken, studentId);
  }

  public async getCleverToken(code: string, clientId: string, redirectURI: string): Promise<Response> {
    return this.externalSchoolIntegration.getToken(code, clientId, redirectURI);
  }
}
